/**
 * Lädt die gelieferten Rümpfe (`models/*.glb`, Auftrag docs/handoffs/2026-09-19-3d-ruempfe.md)
 * und macht daraus dasselbe `VesselModel`, das die prozedurale Geometrie liefert.
 *
 * Solange eine Datei fehlt oder nicht lädt, bleibt der prozedurale Rumpf stehen. Die App
 * soll auch dann laufen, wenn ein Modell kaputt ist: ein fehlender Rumpf darf kein
 * fehlendes Lichterbild sein.
 */
import { Box3, Color, Group, Mesh, MeshStandardMaterial, Object3D, Vector3 } from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { Mount } from '@/core/types'
import type { HullKind, HullMaterials, VesselModel } from './hulls'
import { belegeMaterial } from './oberflaechen'

/** Länge, in der die Modelle gebaut sind. Die App skaliert auf die echte Länge. */
export const NOMINAL_LENGTH_M: Record<HullKind, number> = {
  cargo: 180,
  sailing: 14,
  trawler: 26,
  tug: 32,
  pilot: 18,
  small: 9,
  barge: 70,
  seaplane: 16,
}

const MOUNTS: Mount[] = [
  'mast-fwd',
  'mast-aft',
  'masthead-top',
  'bow',
  'stern',
  'deck',
  'yardarm-port',
  'yardarm-stbd',
  'best-seen',
]

const urls = import.meta.glob('../../models/*.glb', {
  query: '?url',
  import: 'default',
  eager: true,
}) as Record<string, string>

function urlFor(hull: HullKind): string | undefined {
  const key = Object.keys(urls).find((k) => k.endsWith(`/${hull}.glb`))
  return key ? urls[key] : undefined
}

const cache = new Map<HullKind, Group | null>()

/** Lädt alle Rümpfe einmal; danach ist der Wechsel des Zustands ohne Warten. */
export async function preloadHulls(): Promise<void> {
  await Promise.all((Object.keys(NOMINAL_LENGTH_M) as HullKind[]).map((hull) => loadHull(hull)))
}

export async function loadHull(hull: HullKind): Promise<Group | null> {
  const cached = cache.get(hull)
  if (cached !== undefined) return cached
  const url = urlFor(hull)
  if (!url) {
    cache.set(hull, null)
    return null
  }
  try {
    const gltf = await new GLTFLoader().loadAsync(url)
    cache.set(hull, gltf.scene)
    return gltf.scene
  } catch {
    cache.set(hull, null)
    return null
  }
}

export function loadedHull(hull: HullKind): Group | null {
  return cache.get(hull) ?? null
}

/**
 * Klont die Materialien des geladenen Modells und legt die gelieferten Oberflächen auf.
 * `massstab` ist der Faktor auf die Länge des Zustands; er geht in die Wiederholung der
 * Kacheln ein, damit eine Planke auf jedem Schiff gleich breit bleibt.
 */
function materialsOf(root: Object3D, massstab: number): HullMaterials {
  const found = new Map<string, MeshStandardMaterial>()
  const surfaces: NonNullable<HullMaterials['surfaces']> = []
  root.traverse((node) => {
    if (!(node instanceof Mesh)) return
    const cloneMaterial = (source: MeshStandardMaterial): MeshStandardMaterial => {
      let owner: Object3D | null = node
      while (owner && !owner.userData.colorRole) owner = owner.parent
      const role = owner?.userData.colorRole ?? (source.name === 'hull' ? 'hull' : 'superstructure')
      const key = `${source.uuid}:${role}`
      const existing = found.get(key)
      if (existing) return existing
      // GLTF-Klone teilen zunächst ihre Materialien. Die Ansicht eines Fahrzeugs darf
      // weder den Cache noch ein zweites Fahrzeug zwischen Tag und Nacht umschalten.
      const material = source.clone()
      belegeMaterial(material, massstab)
      found.set(key, material)
      surfaces.push({
        material,
        dayColour: material.color.clone(),
        nightColour: new Color(role === 'hull' ? 0x1d2430 : 0x2a3341),
        dayVertexColors: material.vertexColors,
      })
      return material
    }
    node.material = Array.isArray(node.material)
      ? node.material.map(cloneMaterial)
      : cloneMaterial(node.material)
  })
  const list = [...found.values()]
  const hull = list.find((material) => material.name === 'hull' || material.name === 'stahl' || material.name === 'kunststoff') ?? list[0]!
  const superstructure = list.find((material) => material.name === 'superstructure') ?? list[1] ?? hull
  return { hull, superstructure, mast: superstructure, surfaces }
}

/**
 * Baut aus dem geladenen glTF ein Fahrzeug in der gewünschten Länge.
 *
 * Jeder Montagepunkt kommt aus dem Knoten `anchor_<punkt>` des Modells, auch `bow`:
 * die Lieferung führt elf Knoten, einen mehr als die Tabelle im Handoff verlangte.
 * Die Ersatzrechnung darunter greift nur, wenn ein Knoten fehlt, und dann fällt die
 * Prüfung `test/model-loader.test.ts` auf, bevor es jemand im Bild sieht.
 */
export function vesselFromHull(source: Group, hull: HullKind, lengthM: number): VesselModel {
  const scale = lengthM / NOMINAL_LENGTH_M[hull]
  const group = new Group()
  const model = source.clone(true)
  model.scale.setScalar(scale)
  group.add(model)
  model.updateMatrixWorld(true)

  const anchor = (name: string): Vector3 | undefined => {
    const node = model.getObjectByName(`anchor_${name}`)
    return node ? node.getWorldPosition(new Vector3()) : undefined
  }

  const port = anchor('sidelight-port')
  const stbd = anchor('sidelight-stbd')
  const box = new Box3().setFromObject(model)
  const fallback = new Vector3(0, Math.max(0.5, box.max.y * 0.5), 0)
  const anchors = Object.fromEntries(
    MOUNTS.map((mount) => {
      const delivered = anchor(mount)
      if (delivered) return [mount, delivered]
      // Nur für den Fall einer unvollständigen Datei: das Lichterbild bleibt sichtbar.
      if (mount === 'bow' && port) return [mount, new Vector3(0, port.y, port.z)]
      return [mount, fallback.clone()]
    }),
  ) as Record<Mount, Vector3>

  return {
    group,
    materials: materialsOf(model, scale),
    anchors,
    sidelightAnchors: port && stbd ? { port, stbd } : undefined,
    lengthM,
    beamM: box.max.x - box.min.x,
    sidelightOffsetM: port ? Math.abs(port.x) : (box.max.x - box.min.x) * 0.45,
    deckY: box.max.y * 0.2,
    geometries: [],
    shared: true,
  }
}
